/**
 * FormField（08 开发文档清单）：label 与错误关联（htmlFor/aria-describedby），
 * 状态提示不只靠颜色（错误带 ✎ 标记文本）。
 */
import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

export interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** 字段级错误话术（关联 aria-describedby；非仅红框） */
  error?: string;
  hint?: string;
}

export function FormField({ label, error, hint, id, ...rest }: FormFieldProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={`cs-field${error ? ' cs-field--error' : ''}`}>
      <label className='cs-field__label' htmlFor={fieldId}>
        {label}
      </label>
      <input
        id={fieldId}
        className='cs-field__input'
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        {...rest}
      />
      {hint && (
        <p className='cs-field__hint' id={hintId}>
          {hint}
        </p>
      )}
      {error && (
        <p className='cs-field__error' id={errorId} role='alert'>
          <span aria-hidden='true'>✎ </span>
          {error}
        </p>
      )}
    </div>
  );
}

/** 文本域变体（同一套 label/错误关联约定） */
export interface FormTextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  hint?: string;
}

export function FormTextArea({ label, error, hint, id, ...rest }: FormTextAreaProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={`cs-field${error ? ' cs-field--error' : ''}`}>
      <label className='cs-field__label' htmlFor={fieldId}>
        {label}
      </label>
      <textarea
        id={fieldId}
        className='cs-field__input cs-field__textarea'
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        {...rest}
      />
      {hint && (
        <p className='cs-field__hint' id={hintId}>
          {hint}
        </p>
      )}
      {error && (
        <p className='cs-field__error' id={errorId} role='alert'>
          <span aria-hidden='true'>✎ </span>
          {error}
        </p>
      )}
    </div>
  );
}

/** 多行只读展示（后台结构化问答复用） */
export function FieldBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='cs-field'>
      <p className='cs-field__label'>{label}</p>
      <div className='cs-field__static'>{children}</div>
    </div>
  );
}
